// Opening hours as rules (docs/tms-stamdata.md §6.3): what the editor types, checks
// and says back. The API validates the same shape (modules/locations/openingHours.ts).

export type HoursStatus = "unknown" | "known" | "always";

export interface OpeningRule {
  days: number[];
  open: string;
  close: string;
  breaks?: [string, string][];
}

export interface OpeningHours {
  status: HoursStatus;
  rules: OpeningRule[];
}

export const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(":");
  return Number(hours) * 60 + Number(minutes);
}

/** Minutes from midnight as a clock time: 1020 → 17:00, 1440 → 24:00. */
export function formatMinutes(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/** The ISO weekday of a calendar date (YYYY-MM-DD): 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: string): number {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/**
 * A time typed the way people say it: 8 → 08:00, 830 → 08:30, 1630 → 16:30,
 * 16.30 and 16:30 → 16:30, 24 → 23:59. Null when it is not a time.
 */
export function parseTimeInput(input: string): string | null {
  const value = input.trim().replace(/[.,h]/gi, ":");
  if (!value) return null;
  let hours: number;
  let minutes: number;
  const withColon = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (withColon) {
    hours = Number(withColon[1]);
    minutes = Number(withColon[2]);
  } else if (/^\d{1,2}$/.test(value)) {
    hours = Number(value);
    minutes = 0;
  } else if (/^\d{3,4}$/.test(value)) {
    hours = Number(value.slice(0, -2));
    minutes = Number(value.slice(-2));
  } else {
    return null;
  }
  if (hours === 24 && minutes === 0) return "23:59";
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Consecutive days as ranges: [1, 2, 3, 5] → [[1, 3], [5, 5]]. */
export function dayRanges(days: readonly number[]): [number, number][] {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const ranges: [number, number][] = [];
  for (const day of sorted) {
    const last = ranges[ranges.length - 1];
    if (last && day === last[1] + 1) last[1] = day;
    else ranges.push([day, day]);
  }
  return ranges;
}

export interface HoursWords {
  /** Short day name, lower case: "ma". */
  day: (day: number) => string;
  closed: string;
  pause: string;
  unknown: string;
  always: string;
  /** Said after a window that runs past midnight. */
  overnight: string;
}

// Three days or more in a row read as a range (ma–do), two as a pair (za, zo).
function daysText(days: readonly number[], words: HoursWords): string {
  return dayRanges(days)
    .map(([from, to]) =>
      from === to ? words.day(from) : to === from + 1 ? `${words.day(from)}, ${words.day(to)}` : `${words.day(from)}–${words.day(to)}`
    )
    .join(", ");
}

/** The sentence under the editor: "Ma–do 08:00–17:00, pauze 12:00–13:00 · vr 08:00–15:00 · za, zo gesloten". */
export function describeOpeningHours(hours: OpeningHours, words: HoursWords): string {
  if (hours.status === "unknown") return words.unknown;
  if (hours.status === "always") return words.always;

  const parts = [...hours.rules]
    .filter((rule) => rule.days.length > 0)
    .sort((a, b) => Math.min(...a.days) - Math.min(...b.days))
    .map((rule) => {
      const breaks = (rule.breaks ?? []).map(([start, end]) => `, ${words.pause} ${start}–${end}`).join("");
      const overnight = toMinutes(rule.close) < toMinutes(rule.open) ? ` ${words.overnight}` : "";
      return `${daysText(rule.days, words)} ${rule.open}–${rule.close}${breaks}${overnight}`;
    });
  const covered = new Set(hours.rules.flatMap((rule) => rule.days));
  const closed = WEEK_DAYS.filter((day) => !covered.has(day));
  if (closed.length > 0) parts.push(`${daysText(closed, words)} ${words.closed}`);

  const sentence = parts.join(" · ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

export type HoursProblem = "noRules" | "noDays" | "dayTwice" | "sameTime" | "breakOutside";

/** What is wrong with the rules before they go to the API, or null. */
export function findHoursProblem(hours: OpeningHours): HoursProblem | null {
  if (hours.status !== "known") return null;
  if (hours.rules.length === 0) return "noRules";
  const seen = new Set<number>();
  for (const rule of hours.rules) {
    if (rule.days.length === 0) return "noDays";
    for (const day of rule.days) {
      if (seen.has(day)) return "dayTwice";
      seen.add(day);
    }
    if (rule.open === rule.close) return "sameTime";
    const open = toMinutes(rule.open);
    const close = toMinutes(rule.close);
    for (const [start, end] of rule.breaks ?? []) {
      const from = toMinutes(start);
      const to = toMinutes(end);
      const inside = close > open ? from > open && to < close && to > from : to !== from;
      if (!inside) return "breakOutside";
    }
  }
  return null;
}

/** The parts of a day that are open, in minutes from midnight, for the week bar. */
export function openSegments(hours: OpeningHours, day: number): [number, number][] {
  if (hours.status === "always") return [[0, 24 * 60]];
  if (hours.status !== "known") return [];
  const segments: [number, number][] = [];
  const previous = day === 1 ? 7 : day - 1;
  for (const rule of hours.rules) {
    const open = toMinutes(rule.open);
    const close = toMinutes(rule.close);
    if (rule.days.includes(day)) {
      const end = close > open ? close : 24 * 60;
      let start = open;
      for (const [breakStart, breakEnd] of [...(rule.breaks ?? [])].sort((a, b) => toMinutes(a[0]) - toMinutes(b[0]))) {
        segments.push([start, toMinutes(breakStart)]);
        start = toMinutes(breakEnd);
      }
      segments.push([start, end]);
    }
    // A window past midnight runs on into the next morning.
    if (close < open && rule.days.includes(previous)) segments.push([0, close]);
  }
  return segments.sort((a, b) => a[0] - b[0]);
}
